import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, ListGroup, Form, Button, Card } from 'react-bootstrap';
import { FaTrash, FaPlus, FaMinus } from 'react-icons/fa';
import Message from '../components/Message';
import Loader from '../components/Loader';
import { addToCart, removeFromCart, clearCartItems } from '../slices/cartSlice';
import {
  useGetStoreTransferDetailsQuery,
  useCreateStoreTransferMutation,
} from '../slices/storeTransfersApiSlice';
import { useGetCustomersQuery } from '../slices/customersApiSlice';
import { toast } from 'react-toastify';

const StoreTransferAddScreen = () => {
  const { id: storeTransferId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { cartItems } = useSelector((state) => state.cart);

  const [mergedOnce, setMergedOnce] = useState(false);

  const [customerSearch, setCustomerSearch] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [note, setNote] = useState('');
  const [storeTransferDate, setStoreTransferDate] = useState(new Date().toISOString().split('T')[0]);

  const { data: storeTransferData, isLoading: loadingStoreTransfer, error: storeTransferError } =
    useGetStoreTransferDetailsQuery(storeTransferId);

  const { data: customersData, isLoading: loadingCustomers, error: customersError } =
    useGetCustomersQuery({ keyword: customerSearch });

  const [createStoreTransfer, { isLoading: savingStoreTransfer }] = useCreateStoreTransferMutation();

  // Normalize cart items (convert _id → product)
  const normalizeCart = (items) =>
    items.map((item) => ({
      ...item,
      product: item.product || item._id, // FIXED
    }));

  // Merge logic using productId
  const mergeStoreTransferWithCart = (storeTransferItems, cartItems) => {
    const map = new Map();

    normalizeCart(cartItems).forEach((item) => {
      map.set(item.product.toString(), { ...item });
    });

    storeTransferItems.forEach((item) => {
      map.set(item.product.toString(), { ...item });
    });

    return Array.from(map.values());
  };

  // Load storeTransfer data and merge ONCE
  useEffect(() => {
    if (storeTransferData && !mergedOnce) {
      const merged = mergeStoreTransferWithCart(storeTransferData.storeTransferItems, cartItems);

      merged.forEach((item) => {
        dispatch(
          addToCart({
            ...item,
            product: item.product,
            qty: item.qty,
          })
        );
      });

      setMergedOnce(true);
    }
  }, [storeTransferData, mergedOnce]);

  // Filter customers
  useEffect(() => {
    if (customersData?.customers) {
      const filtered = customersData.customers.filter((customer) =>
        customer.name.toLowerCase().includes(customerSearch.toLowerCase())
      );
      setFilteredCustomers(filtered);
    }
  }, [customerSearch, customersData]);

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch(customer.name);
    setFilteredCustomers([]);
  };

  const increaseQtyHandler = (item) => {
    dispatch(addToCart({ ...item, qty: item.qty + 1 }));
  };

  const decreaseQtyHandler = (item) => {
    if (item.qty > 1) {
      dispatch(addToCart({ ...item, qty: item.qty - 1 }));
    } else {
      toast.error('Quantity cannot be less than 1');
    }
  };

  const removeFromCartHandler = (productId) => {
    dispatch(removeFromCart(productId));
  };

  const createStoreTransferHandler = async () => {
    try {
      if (!selectedCustomer) {
        toast.error('Please select a customer');
        return;
      }

      if (cartItems.length === 0) {
        toast.error('Your cart is empty');
        return;
      }

      const storeTransferItems = normalizeCart(cartItems).map((item) => ({
        product: item.product,
        qty: item.qty,
      }));

      await createStoreTransfer({
        storeTransferItems,
        customerDetails: selectedCustomer._id,
        note,
        status: 'Completed',
        date: storeTransferDate,
      }).unwrap();

      toast.success('New storeTransfer created successfully');

      dispatch(clearCartItems());
      navigate('/StoreAdmin/storeTransferlist');
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to create storeTransfer');
    }
  };

  return (
    <Row>
      <Col md={8}>
        <h1>Create New StoreTransfer (Based on StoreTransfer #{storeTransferId})</h1>

        {loadingStoreTransfer ? (
          <Loader />
        ) : storeTransferError ? (
          <Message variant="danger">{storeTransferError?.data?.message}</Message>
        ) : (
          <>
            {cartItems.length === 0 ? (
              <Message>
                Your cart is empty.{' '}
                <Button onClick={() => navigate('/house/HomeScreen')}>Go Back</Button>
              </Message>
            ) : (
              <ListGroup variant="flush">
                {normalizeCart(cartItems).map((item) => (
                  <ListGroup.Item key={item.product}>
                    <Row>
                      <Col md={2}>
                        {item.images?.length > 0 && (
                          <img
                            src={item.images[0]}
                            alt="Product"
                            style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                          />
                        )}
                      </Col>

                      <Col>{item.name}</Col>

                      <Col md={2}>D{item.price.toFixed(2)}</Col>

                      <Col md={3}>
                        <div className="d-flex align-items-center">
                          <Button variant="light" onClick={() => decreaseQtyHandler(item)}>
                            <FaMinus />
                          </Button>
                          <span className="mx-2">{item.qty}</span>
                          <Button variant="light" onClick={() => increaseQtyHandler(item)}>
                            <FaPlus />
                          </Button>
                        </div>
                      </Col>

                      <Col md={2}>D{(item.qty * item.price).toFixed(2)}</Col>

                      <Col md={1}>
                        <Button variant="light" onClick={() => removeFromCartHandler(item.product)}>
                          <FaTrash />
                        </Button>
                      </Col>
                    </Row>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </>
        )}
      </Col>

      <Col md={4}>
        <Card>
          <ListGroup variant="flush">
            <ListGroup.Item>
              <h2>Select Customer</h2>

              {loadingCustomers ? (
                <Loader />
              ) : customersError ? (
                <Message variant="danger">{customersError?.data?.message}</Message>
              ) : (
                <>
                  <Form.Group controlId="customerSearch">
                    <Form.Label>Search Customer</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Type customer name"
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                    />
                  </Form.Group>

                  {filteredCustomers.length > 0 && (
                    <ListGroup>
                      {filteredCustomers.map((customer) => (
                        <ListGroup.Item
                          key={customer._id}
                          action
                          onClick={() => handleCustomerSelect(customer)}
                        >
                          {customer.name} ({customer.company})
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}

                  {selectedCustomer && (
                    <div className="mt-3">
                      <strong>Selected Customer:</strong>
                      <p>
                        {selectedCustomer.name} ({selectedCustomer.company})<br />
                        Phone: {selectedCustomer.telephone}<br />
                        Address: {selectedCustomer.address}<br />
                        SERIAL: {selectedCustomer.serial}
                      </p>

                      <Form.Group controlId="note">
                        <Form.Label>Note</Form.Label>
                        <Form.Control
                          type="text"
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                        />
                      </Form.Group>

                      <Form.Group controlId="storeTransferDate" className="mt-2">
                        <Form.Label>Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={storeTransferDate}
                          onChange={(e) => setStoreTransferDate(e.target.value)}
                        />
                      </Form.Group>
                    </div>
                  )}
                </>
              )}
            </ListGroup.Item>

            <ListGroup.Item>
              <Button
                type="button"
                className="btn-block"
                disabled={savingStoreTransfer || cartItems.length === 0 || !selectedCustomer}
                onClick={createStoreTransferHandler}
              >
                {savingStoreTransfer ? 'Processing...' : 'Create StoreTransfer'}
              </Button>
            </ListGroup.Item>
          </ListGroup>
        </Card>
      </Col>
    </Row>
  );
};

export default StoreTransferAddScreen;
